import { useEffect, lazy, Suspense, useRef, useState, FC, KeyboardEvent } from 'react';
import { useDebounce, useOnClickOutside } from '../../../helpers';
import { Estates, EstateType } from '../../../Enums';
import { Autocomplete } from '../../../Enums/AutocompleteEnums';

import { FilterEstateTags } from '../../../services/generatedApi';
import { useForm } from 'react-hook-form';

import DropDown from './DropDown';
const MobileSearchTags = lazy(() => import('./MobileSearchTags'));
const DesktopSearchTags = lazy(() => import('./DesktopSearchTags'));
const AcReultsMuni = lazy(() => import('./AcResultsMuni'));
const AcReultsArea = lazy(() => import('./AcResultsArea'));
const AcReultsCounty = lazy(() => import('./AcResultsCounty'));
const AcResultsEstates = lazy(() => import('../AcResults/AcResultsEstates'));

import useMediaQuery from '../../CustomHooks/useMediaQuery';
import useAutocomplete from '../../CustomHooks/useAutocomplete';

const basename = '/till-salu/';

const estateTypes = Object.entries(EstateType).filter((estateType) => estateType[1] !== EstateType.OwnedApartment && estateType[1] !== EstateType.Premises).map(([_, value]) => value);
const roomsMinNumber = ['2', '3', '4', '5', '6'];

type Props = {
  shouldShowSearchResults: (
    hideAC: boolean,
    acResults: FilterEstateTags | null | undefined
  ) => boolean;
  shouldShowNoSearchResults: (
    hideAC: boolean,
    acResults: FilterEstateTags | null | undefined
  ) => boolean;
};

const SearchForm: FC<Props> = ({
  shouldShowSearchResults,
  shouldShowNoSearchResults,
}) => {
  const { register, handleSubmit, watch, setValue } = useForm({});
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setHideAc(true));
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoSearchResults, setShowNoSearchResults] = useState(false);
  const [hideAC, setHideAc] = useState(false);
  const [acResults, setACResults] = useState<FilterEstateTags | null>();
  let searchTerm = watch('searchTerm', '');
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 200);
  const [freetext, setFreetext] = useState<string>('');
  const { data: resp } = useAutocomplete(freetext);
  const isMediumScreen = useMediaQuery('(min-width: 64rem)');
  const mergedPrettyNamesContainer = useRef<HTMLDivElement>(null);

  const methods = useForm({
    defaultValues: {
      searchTerm: '',
      municipality: [] as { name: string; prettyName: string; type: string }[],
      area: [] as {
        areaId: string;
        name: string;
        prettyName: string;
        type: string;
        areaMuni: string;
      }[],
      county: [] as { name: string; prettyName: string; type: string }[],
      estateType: '',
      roomsMin: '',
      roomsMax: '10',
    },
  });

  const [mergedPrettyNames, setMergedPrettyNames] = useState<
    { prettyName: string; type: string }[]
  >([]);

  useEffect(() => {
    const subscription = methods.watch((value) => {
      const municipalities = value.municipality.map((item) => ({
        prettyName: item.prettyName,
        type: item.type,
      }));
      const areas = value.area.map((item) => ({
        prettyName: item.prettyName,
        type: item.type,
      }));
      const counties = value.county.map((item) => ({
        prettyName: item.prettyName,
        type: item.type,
      }));

      const merged = [...municipalities, ...areas, ...counties];
      setMergedPrettyNames(merged);
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    setACResults(resp);
  }, [resp]);

  useEffect(() => {
    const query = debouncedSearchTerm.trim();
    if (!query || query.length < 2) {
      setACResults(null);
    } else {
      setFreetext(query);
      setHideAc(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setShowSearchResults(shouldShowSearchResults(hideAC, acResults));
    setShowNoSearchResults(shouldShowNoSearchResults(hideAC, acResults));
  }, [hideAC, acResults]);

  const onSubmit = () => {
    const { municipality, area, county, estateType, roomsMin, roomsMax } =
      methods.getValues();
    let queryString = '?';

    if (municipality.length > 0) {
      queryString += `municipality=${encodeURIComponent(
        municipality.map((item) => item.name).join(',')
      )}&`;
    }
    if (area.length > 0) {
      queryString += `areaIds=${encodeURIComponent(
        area.map((item) => item.areaId).join(',')
      )}&`;
    }
    if (area.length > 0) {
      queryString += `areaNames=${encodeURIComponent(
        area.map((item) => item.name).join(',')
      )}&`;
    }
    if (county.length > 0) {
      queryString += `county=${encodeURIComponent(
        county.map((item) => item.name).join(',')
      )}&`;
    }
    if (estateType) {
      queryString += `estateType=${encodeURIComponent(estateType)}&`;
    }
    if (roomsMin) {
      queryString += `roomsMin=${encodeURIComponent(roomsMin)}&`;
      queryString += `roomsMax=${encodeURIComponent(roomsMax)}&`;
    }
    if (queryString.endsWith('&') || queryString.endsWith('?')) {
      queryString = queryString.slice(0, -1);
    }

    window.location.href = basename + queryString;
  };

  const handlePick = (
    name: string,
    typeName: string,
    type: 'municipality' | 'area' | 'county',
    id?: string,
    areaMuni?: string
  ) => {
    setValue('searchTerm', '');

    setShowSearchResults(false);
    setHideAc(true);

    if (type === 'municipality') {
      const currentMunicipalities = methods.getValues('municipality');
      if (!currentMunicipalities.some((item) => item.name === name)) {
        const updatedMunicipalities = [
          ...currentMunicipalities,
          { name, prettyName: `${name} (${typeName})`, type: type },
        ];
        methods.setValue('municipality', updatedMunicipalities);
      }
    } else if (type === 'area') {
      const currentAreas = methods.getValues('area');
      if (!currentAreas.some((area) => area.areaId === id)) {
        const updatedAreas = [
          ...currentAreas,
          {
            areaId: id!,
            name,
            prettyName: `${name} (${typeName})`,
            type: type,
            areaMuni: areaMuni!,
          },
        ];
        methods.setValue('area', updatedAreas);
      }
    } else if (type === 'county') {
      const currentCounties = methods.getValues('county');
      if (!currentCounties.some((item) => item.name === name)) {
        const updatedCounties = [
          ...currentCounties,
          { name, prettyName: `${name} (${typeName})`, type: type },
        ];
        methods.setValue('county', updatedCounties);
      }
    }
  };

  const removeFromMergedPrettyNames = (type: string, prettyName: string) => {
    const updatedMergedPrettyNames = mergedPrettyNames.filter(
      (item) => item.prettyName !== prettyName
    );
    setMergedPrettyNames(updatedMergedPrettyNames);

    // Update defaultValues in methods based on type and prettyName
    if (type === 'municipality') {
      const updatedDefaultValues = {
        ...methods.getValues(),
        municipality: methods
          .getValues('municipality')
          .filter((item) => item.prettyName !== prettyName),
      };
      methods.reset(updatedDefaultValues);
    } else if (type === 'area') {
      const updatedDefaultValues = {
        ...methods.getValues(),
        area: methods
          .getValues('area')
          .filter((item) => item.prettyName !== prettyName),
      };
      methods.reset(updatedDefaultValues);
    } else if (type === 'county') {
      const updatedDefaultValues = {
        ...methods.getValues(),
        county: methods
          .getValues('county')
          .filter((item) => item.prettyName !== prettyName),
      };
      methods.reset(updatedDefaultValues);
    }
  };

  useEffect(() => {
    if (mergedPrettyNamesContainer.current) {
      mergedPrettyNamesContainer.current.scrollLeft =
        mergedPrettyNamesContainer.current.scrollWidth;
    }
  }, [mergedPrettyNames]);

  const [focusInput, setFocusInput] = useState<boolean>(false);
  const [overflow, setOverflow] = useState<string>('lg:overflow-visible');
  useEffect(() => {
    if (mergedPrettyNames.length > 0) {
      if (focusInput) {
        setOverflow('lg:overflow-hidden');
      } else {
        setTimeout(() => {
          setOverflow('lg:overflow-visible');
        }, 300);
      }
    }
  }, [focusInput]);

  return (
    <div
      id="searchfield-input-wrapper"
      className="lg:flex flex-grow lg:pt-1 pb-2 lg:pb-1"
    >
      <form 
        className="w-full" 
        onSubmit={handleSubmit(onSubmit)} 
        role="search"
        aria-label="Bostadssökning"
      >
        <div
          className={`flex flex-col lg:grid ${
            focusInput
              ? mergedPrettyNames.length > 0
                ? 'lg:grid-cols-[6fr_2fr_1fr]'
                : 'lg:grid-cols-[4fr_4fr_1fr]'
              : 'lg:grid-cols-[4fr_4fr_1fr]'
          } lg:bg-white lg:rounded-[60px] lg:items-center lg:py-3 lg:px-4 gap-1 transition-all w-full`}
        >
          <div className="relative">
            <div className="flex flex-row items-center gap-2 lg:gap-4 psuedo-border-after bg-white lg:shadow-none px-4 lg:pl-1 border-none rounded-full w-full outline-none">
              <img
                src="/Images/Icons/location-blue.svg"
                alt=""
                aria-hidden="true"
                className="w-5.5 h-auto"
              />
              <div className="lg:flex lg:flex-col w-full">
                <label
                  id="searchfield-label"
                  className="lg:block hidden font-icb text-blue700 text-sm"
                >
                  {Autocomplete.LocationPick}
                </label>
                <div className="flex lg:justify-start lg:gap-2">
                  {mergedPrettyNames &&
                    mergedPrettyNames.length > 0 &&
                    isMediumScreen && (
                      <Suspense fallback={<></>}>
                        <DesktopSearchTags
                          removeFromMergedPrettyNames={
                            removeFromMergedPrettyNames
                          }
                          mergedPrettyNames={mergedPrettyNames}
                          mergedPrettyNamesContainer={
                            mergedPrettyNamesContainer
                          }
                        />
                      </Suspense>
                    )}

                  <input
                    id="searchfield-input"
                    autoComplete="off"
                    className="bg-white placeholder:opacity-100 px-1 lg:px-0 py-[11px] lg:py-1 rounded-3xl lg:rounded-none w-full min-w-[100px] text-base lg:text-black500 placeholder:text-grey400 outline-none searchfield-input-hero"
                    type="search"
                    value={searchTerm ?? ''}
                    placeholder={Estates.SearchPlaceholder}
                    aria-labelledby="searchfield-label"
                    aria-autocomplete="list"
                    aria-expanded={showSearchResults}
                    aria-controls={showSearchResults ? "ac-result-list" : undefined}
                    onClick={() => setHideAc(false)}
                    {...(register('searchTerm'),
                    {
                      onFocus: () => setFocusInput(true),
                      onBlur: () => setFocusInput(false),
                      onChange: (e) => setValue('searchTerm', e.target.value),
                    })}
                  />
                </div>
              </div>
            </div>
            {mergedPrettyNames &&
              mergedPrettyNames.length > 0 &&
              !isMediumScreen && (
                <Suspense fallback={<></>}>
                  <MobileSearchTags
                    removeFromMergedPrettyNames={removeFromMergedPrettyNames}
                    mergedPrettyNames={mergedPrettyNames}
                  />
                </Suspense>
              )}
            <div ref={ref}>
              {showSearchResults && (
                <div
                  id="ac-result-list"
                  className="top-12 lg:top-[66px] left-0 z-50 absolute bg-grey50 shadow-bs80 rounded-2xl w-full max-h-sm overflow-hidden overflow-y-auto"
                >
                  <Suspense fallback={<></>}>
                    {!!acResults?.municipalities.length && (
                      <AcReultsMuni
                        acResults={acResults}
                        handlePick={handlePick}
                      />
                    )}
                    {!!acResults?.areas.length && (
                      <AcReultsArea
                        acResults={acResults}
                        handlePick={handlePick}
                      />
                    )}
                    {!!acResults?.counties.length && (
                      <AcReultsCounty
                        acResults={acResults}
                        handlePick={handlePick}
                      />
                    )}
                    {!!acResults?.estates.length && (
                      <AcResultsEstates acResults={acResults.estates} />
                    )}
                  </Suspense>
                </div>
              )}
              {showNoSearchResults && (
                <div className="relative">
                  <div className="top-[1px] lg:top-[14px] left-1/2 z-50 absolute bg-grey50 shadow-bs80 p-3 rounded-2xl w-[95vw] lg:w-full max-h-xl -translate-x-1/2 overflow-hidden overflow-y-auto">
                    Din sökning på{' '}
                    <span className="font-icb">{searchTerm}</span> gav inga
                    resultat.
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className={`flex flex-col gap-1 lg:gap-0 lg:grid transition-all ${overflow} ${
              focusInput
                ? mergedPrettyNames.length > 0
                  ? 'lg:grid-cols-[220px_220px]'
                  : 'lg:grid-cols-2'
                : 'lg:grid-cols-2'
            }`}
          >
            <DropDown
              name="estateType"
              options={estateTypes}
              setValue={methods.setValue}
              getValues={methods.getValues}
              labelText={Autocomplete.EstateTypeLabel}
              placeholderText={Autocomplete.PickType}
              icon="/Images/Icons/SearchStartPage/estate-type-icon.svg"
            />
            <DropDown
              name="roomsMin"
              options={roomsMinNumber}
              setValue={methods.setValue}
              getValues={methods.getValues}
              labelText={Autocomplete.RoomsMin}
              placeholderText={Autocomplete.PickRoom}
              icon="/Images/Icons/SearchStartPage/room-icon.svg"
              psuedo
            />
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            type="submit"
            className="flex justify-center items-center border-2 bg-blue700 mt-1 lg:mt-0 lg:px-2 p-2 border-blue700 rounded-3xl w-full lg:w-28 font-icb text-white dark:text-white hover-darken-blue"
          >
            {Autocomplete.Search}
            <img
              width="16"
              height="16"
              className="ml-3 w-4"
              src="/Images/Icons/search-icon.svg"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchForm;
